/* In-memory state of the MockProvider, deep-cloned from contracts/seed.json. */
import seedJson from "../../../../contracts/seed.json";
import type * as T from "../types";
import { countryOf, siDefaults } from "../../domain/country";
import { gccDemoCompanies, gccDemoSummaries } from "./gccDemos";

/** Shape of contracts/seed.json (only what the mock needs). */
export interface SeedFile {
  today: string;
  now: string;
  tenant: T.Tenant;
  employees: T.Employee[];
  settings: Omit<T.Settings, "tenant">;
  payrollHistory: T.PayrollHistoryItem[];
  payrollRun: T.PayrollRun;
  leaves: T.Leave[];
  loans: T.Loan[];
  letters: T.Letter[];
  expenses: T.Expense[];
  assets: T.Asset[];
  sites?: T.WorkSite[];
  offboarding: T.Offboarding[];
  onboarding: T.Onboarding[];
  govTransactions: T.GovTransaction[];
  cases: T.Case[];
  candidates: T.Candidate[];
  goals: T.Goal[];
  audit?: T.AuditEntry[];
  subscription: T.Subscription;
  payments: T.Payment[];
  tenants: T.TenantSummary[];
  bankAccounts: T.BankAccount[];
  planPrices: Record<string, number>;
  ai: { providers: T.AiProvider[]; residency: boolean; budget: number; credits: Record<string, number>; used: number; limit: number; features: T.AiFeature[] };
  leavePolicies: T.LeavePolicy[];
  bands: T.Band[];
  devices: T.Device[];
  deviceUserMap: T.DeviceUserMap[];
  punches: T.Punch[];
  deviceUserNames: Record<string, string>;
  deviceCommands: T.DeviceCommand[];
  users: T.User[];
  demoPassword: string;
  /** Ready-made company per business category (summary rows are in `tenants`). */
  demoCompanies?: DemoCompany[];
}

export interface DemoCompany {
  tenant: T.Tenant;
  attendance: Pick<T.AttendancePolicy, "shiftStart" | "shiftEnd" | "weekendDays">;
  subscription: T.Subscription;
  users: T.User[];
  employees: T.Employee[];
  sites: T.WorkSite[];
  leaves: T.Leave[];
  payrollHistory: T.PayrollHistoryItem[];
}

export const SEED: SeedFile = seedJson as unknown as SeedFile;
export const PLATFORM = "platform";

/** Device with server-only fields (never returned to callers). */
export interface DeviceRec extends T.Device {
  tenantId: string; // "" = unassigned (pending)
  secret: string;
  attlogStamp: string;
}
/** Payment with server-only fields. */
export interface PaymentRec extends T.Payment {
  seq: number;
  aiPack: number;
}
export interface StoredFile { ref: T.FileRef; blob: Blob; tenantId: string }

/** Outbox entry of the mock mailer (the Go API keeps the same fields in email_outbox). */
export interface MockEmail extends T.EmailLog { html: string; text: string; idemKey: string; unsubscribe: string }
/** Single-use token (stored hashed in Go; the mock keeps the raw value, never exposed). */
export interface AuthTokenRec { token: string; purpose: "verify" | "reset" | "invite"; userId: string; email: string; expiresAt: number; usedAt: number | null }
export interface MockEmailProvider extends Omit<T.EmailProviderSettings, "hasSecret"> { secret: string }
export interface MockEmailState {
  provider: MockEmailProvider; fallback: MockEmailProvider | null;
  fromName: string; fromAddress: string; replyTo: string; sandboxTo: string; dailyLimit: number;
  source: "env" | "database"; updatedAt?: string; updatedBy?: string;
}
export interface MockStorageState extends Omit<T.StorageConfig, "accessKeyId" | "hasSecret" | "status" | "stats" | "dir"> { accessKeyId: string; secret: string }
export interface AiPlatformState { providers: T.AiProvider[]; residency: boolean; budget: number; credits: Record<string, number>; features: T.AiFeature[] }
export interface AiTenantState { used: number; limit: number; features: T.AiFeature[] }

export interface MockState {
  tenantId: string;
  settings: T.Settings;
  employees: T.Employee[];
  history: T.PayrollHistoryItem[];
  run: T.PayrollRun;
  leaves: T.Leave[];
  loans: T.Loan[];
  letters: T.Letter[];
  expenses: T.Expense[];
  assets: T.Asset[];
  sites: T.WorkSite[];
  offboarding: T.Offboarding[];
  onboarding: T.Onboarding[];
  gov: T.GovTransaction[];
  cases: T.Case[];
  candidates: T.Candidate[];
  goals: T.Goal[];
  audit: T.AuditEntry[];
  /** Audit log of the platform tenant (admin actions). */
  platformAudit: T.AuditEntry[];
  subscription: T.Subscription;
  payments: PaymentRec[];
  tenants: T.TenantSummary[];
  bankAccounts: T.BankAccount[];
  planPrices: Record<string, number>;
  aiPlatform: AiPlatformState;
  aiTenant: AiTenantState;
  leavePolicies: T.LeavePolicy[];
  devices: DeviceRec[];
  mappings: T.DeviceUserMap[];
  punches: T.Punch[];
  dedupe: Set<string>;
  /** "deviceId|pin" → name reported by the device (OPERLOG / USERINFO). */
  deviceUsers: Record<string, string>;
  commands: T.DeviceCommand[];
  users: T.User[];
  demoPassword: string;
  files: Map<string, StoredFile>;
  /** userId → notification id → content signature at the time it was read. */
  notifReads: Record<string, Record<string, string>>;
  seedLeaveMax: number;
  counters: Record<string, number>;
  revoked: Set<string>;

  /* ---- email, storage, accounts (shared across tenants) ---- */
  outbox: MockEmail[];
  suppressions: Record<string, T.Suppression>;
  emailCfg: MockEmailState;
  storageCfg: MockStorageState;
  authTokens: AuthTokenRec[];
  /** userId → password for users created after the seed (seed users use demoPassword). */
  passwords: Record<string, string>;
  /** userIds whose email is not verified yet. */
  unverified: Set<string>;
  /** userId → token version (password reset/change revokes older tokens). */
  tokenVersions: Record<string, number>;
  /** rate-limit key → hit timestamps (ms). */
  rateHits: Record<string, number[]>;
  /** Per-tenant: disabled notification preference keys. */
  notifPrefs: Record<string, boolean>;
}

/** Fields shared by every tenant state of one MockProvider (platform-wide data). */
export const SHARED_KEYS = ["platformAudit", "payments", "tenants", "bankAccounts", "planPrices", "aiPlatform", "leavePolicies", "devices", "users", "demoPassword",
  "files", "notifReads", "revoked", "outbox", "suppressions", "emailCfg", "storageCfg", "authTokens", "passwords", "unverified", "tokenVersions", "rateHits"] as const;

export const DEFAULT_EMAIL: MockEmailState = {
  provider: { provider: "log", host: "", port: 0, security: "", authMethod: "", username: "", region: "", domain: "", messageStream: "", endpoint: "", secret: "" },
  fallback: null, fromName: "StartHR", fromAddress: "noreply@starthr.local", replyTo: "", sandboxTo: "", dailyLimit: 0, source: "env",
};
export const DEFAULT_STORAGE: MockStorageState = {
  mode: "memory", bucket: "", region: "", endpoint: "", pathStyle: false, prefix: "", accessKeyId: "", secret: "", useInstanceRole: false, presign: false, source: "env",
};

/** A fresh, empty company (self-signup): per-tenant data is new, platform data is shared with `root`. */
export function createTenantState(root: MockState, tenant: T.Tenant, today: string, plan: string): MockState {
  const st = {
    tenantId: tenant.id,
    settings: { ...structuredClone(root.settings), tenant: { ...tenant, country: countryOf(tenant.country).code } },
    employees: [], history: [], run: { id: today.slice(0, 7), period: today.slice(0, 7), step: 0, approved: false, exported: false, paid: false, mudadAck: false, adjustments: {} },
    leaves: [], loans: [], letters: [], expenses: [], assets: [], sites: [], offboarding: [], onboarding: [], gov: [], cases: [], candidates: [], goals: [], audit: [],
    subscription: { plan, cycle: "m", seats: 10, status: "trial", trialStart: today, trialDays: 30, activeUntil: null, aiPack: 0 },
    aiTenant: { used: 0, limit: root.aiPlatform.credits[plan] ?? 0, features: structuredClone(root.aiPlatform.features) },
    mappings: [], punches: [], dedupe: new Set<string>(), deviceUsers: {}, commands: [], seedLeaveMax: 0, counters: {}, notifPrefs: {},
  } as unknown as MockState;
  for (const k of SHARED_KEYS) {
    Object.defineProperty(st, k, { enumerable: true, get: () => root[k], set: (v) => { (root as unknown as Record<string, unknown>)[k] = v; } });
  }
  return st;
}

/** Every demo company: the Saudi ones of the seed plus the derived GCC ones (gccDemos.ts). */
export function allDemoCompanies(s: SeedFile): DemoCompany[] {
  return [...(s.demoCompanies ?? []), ...gccDemoCompanies(s)];
}

/** The demo companies' states (same contents as the Go seed's loadDemoCompany). */
export function createDemoStates(root: MockState, seedIn: SeedFile = SEED): MockState[] {
  const s = structuredClone(seedIn);
  return allDemoCompanies(s).map((c) => {
    const st = createTenantState(root, c.tenant, s.today, c.subscription.plan);
    const country = countryOf(c.tenant.country).code;
    st.settings = { ...st.settings, tenant: { ...c.tenant, country }, attendance: { ...st.settings.attendance, ...c.attendance } };
    if (country !== "SA") st.settings.gosi = siDefaults(country);
    st.subscription = c.subscription;
    st.run = { ...st.run, id: s.payrollRun.period, period: s.payrollRun.period };
    st.employees = c.employees.map((e) => ({ ...e, documents: e.documents ?? [], dependants: e.dependants ?? [] }));
    st.sites = c.sites.map((x) => ({ ...x, employeeIds: x.employeeIds ?? [] }));
    st.leaves = c.leaves;
    st.history = c.payrollHistory;
    st.seedLeaveMax = c.leaves.reduce((m, l) => Math.max(m, l.id), 0);
    return st;
  });
}

export function createState(seedIn: SeedFile = SEED): MockState {
  const s = structuredClone(seedIn);
  const tid = s.tenant.id;
  const crToTenant: Record<string, string> = {};
  for (const t of s.tenants) crToTenant[t.cr] = t.id;
  return {
    tenantId: tid,
    settings: { tenant: { ...s.tenant, category: s.tenant.category ?? "", country: countryOf(s.tenant.country).code }, ...s.settings, attendance: { ...s.settings.attendance, weekendDays: s.settings.attendance.weekendDays ?? [] } },
    employees: s.employees.map((e) => ({ ...e, documents: e.documents ?? [], dependants: e.dependants ?? [] })),
    history: s.payrollHistory,
    run: { ...s.payrollRun, adjustments: s.payrollRun.adjustments ?? {} },
    leaves: s.leaves,
    loans: s.loans,
    letters: s.letters,
    expenses: s.expenses,
    assets: s.assets,
    sites: (s.sites ?? []).map((x) => ({ ...x, employeeIds: x.employeeIds ?? [] })),
    offboarding: s.offboarding,
    onboarding: s.onboarding,
    gov: s.govTransactions,
    cases: s.cases,
    candidates: s.candidates,
    goals: s.goals,
    audit: s.audit ?? [],
    platformAudit: [],
    subscription: s.subscription,
    payments: s.payments.map((p) => {
      const i = p.id.lastIndexOf("-");
      return { ...p, tenantId: p.tenantId || crToTenant[p.cr] || "", seq: i >= 0 ? Number(p.id.slice(i + 1)) || 0 : 0, aiPack: 0 };
    }),
    tenants: [...s.tenants, ...gccDemoSummaries(s, gccDemoCompanies(s))].map((t) => ({ ...t, category: t.category ?? "", country: countryOf(t.country).code })),
    bankAccounts: s.bankAccounts,
    planPrices: s.planPrices,
    aiPlatform: { providers: s.ai.providers, residency: s.ai.residency, budget: s.ai.budget, credits: s.ai.credits, features: structuredClone(s.ai.features) },
    aiTenant: { used: s.ai.used, limit: s.ai.limit, features: s.ai.features },
    leavePolicies: s.leavePolicies,
    devices: s.devices.map((d) => ({ ...d, tenantId: d.claimed ? tid : "", secret: d.hasSecret ? "seeded-secret" : "", attlogStamp: "" })),
    mappings: s.deviceUserMap,
    punches: s.punches,
    dedupe: new Set(s.punches.map((p) => p.dedupeKey)),
    deviceUsers: s.deviceUserNames ?? {},
    commands: s.deviceCommands.map((c) => ({ ...c, payload: c.payload ?? {} })),
    users: [...s.users, ...allDemoCompanies(s).flatMap((c) => c.users.map((u) => ({ ...u, tenantId: c.tenant.id })))],
    demoPassword: s.demoPassword,
    files: new Map(),
    notifReads: {},
    seedLeaveMax: s.leaves.reduce((m, l) => Math.max(m, l.id), 0),
    counters: {},
    revoked: new Set(),
    outbox: [],
    suppressions: {},
    emailCfg: structuredClone(DEFAULT_EMAIL),
    storageCfg: structuredClone(DEFAULT_STORAGE),
    authTokens: [],
    passwords: {},
    unverified: new Set(),
    tokenVersions: {},
    rateHits: {},
    notifPrefs: {},
  };
}
